import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  Chip,
  Alert,
  TextField
} from '@mui/material';
import EbaySearchCombined from './EbaySearchCombined';
import API_BASE_URL from '../config/apiBaseUrl';
import { fetchJson } from '../services/authFetch';
const OPISTO_BATCH_STORAGE_KEY = 'opistoBatchSyncState';

const createBatchState = (overrides = {}) => ({
  status: 'idle',
  processed: 0,
  total: 0,
  nextOffset: 0,
  currentOem: null,
  lastProcessedOem: null,
  synced: 0,
  notFound: 0,
  errorCount: 0,
  lastError: null,
  startedAt: null,
  lastUpdatedAt: null,
  ...overrides
});

const loadSavedBatchState = () => {
  try {
    const rawValue = localStorage.getItem(OPISTO_BATCH_STORAGE_KEY);
    if (!rawValue) {
      return createBatchState();
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || typeof parsedValue !== 'object') {
      return createBatchState();
    }

    return createBatchState(parsedValue);
  } catch (error) {
    console.warn('Impossible de restaurer l etat de synchro Opisto:', error);
    return createBatchState();
  }
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-watch-tabpanel-${index}`}
      aria-labelledby={`market-watch-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function MarketWatch() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [opistoSyncing, setOpistoSyncing] = useState(false);
  const [opistoBatchSyncing, setOpistoBatchSyncing] = useState(false);
  const [opistoOem, setOpistoOem] = useState('');
  const [opistoBatchProgress, setOpistoBatchProgress] = useState(() => loadSavedBatchState());
  const [data, setData] = useState({
    summary: null,
    opportunities: [],
    emergingOems: [],
    sellers: [],
    metrics: null
  });

  const [daysFilter, setDaysFilter] = useState(30);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const opistoBatchStopRequested = useRef(false);

  // Charger les données
  useEffect(() => {
    loadAllData();
    const interval = setInterval(loadAllData, 5 * 60 * 1000); // Refresh toutes les 5 minutes
    return () => clearInterval(interval);
  }, [daysFilter]);

  useEffect(() => {
    localStorage.setItem(OPISTO_BATCH_STORAGE_KEY, JSON.stringify(opistoBatchProgress));
  }, [opistoBatchProgress]);

  useEffect(() => {
    if (opistoBatchProgress.status === 'running' && !opistoBatchSyncing) {
      setOpistoBatchProgress((previousState) => createBatchState({
        ...previousState,
        status: 'paused',
        currentOem: null,
        lastUpdatedAt: new Date().toISOString()
      }));
    }
  }, [opistoBatchProgress.status, opistoBatchSyncing]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [summary, opportunities, emerging, sellers, metrics] = await Promise.all([
        fetchData('/market-watch/summary'),
        fetchData('/market-watch/opportunities?limit=100'),
        fetchData(`/market-watch/emerging-oems?days=${daysFilter}`),
        fetchData('/market-watch/sellers'),
        fetchData(`/market-watch/metrics?days=${daysFilter}`)
      ]);

      setData({
        summary: summary?.data || null,
        opportunities: opportunities?.data || [],
        emergingOems: emerging?.data || [],
        sellers: sellers?.data || [],
        metrics: metrics || null
      });
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setErrorMessage('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (endpoint) => {
    return fetchJson(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  };

  const handleOpistoSync = async () => {
    setOpistoSyncing(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const normalizedOem = opistoOem.trim().toUpperCase();
      if (normalizedOem === '') {
        setErrorMessage('Renseigne une refOEM pour lancer une synchro Opisto ciblée.');
        setOpistoSyncing(false);
        return;
      }

      await fetchJson(`${API_BASE_URL}/import/opisto/algolia-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ oemReferences: [normalizedOem] })
      });

      setSuccessMessage(
        `Synchro Opisto terminée pour ${normalizedOem}`
      );
    } catch (error) {
      console.error('Erreur synchro Opisto:', error);
      setErrorMessage(`Erreur lors de la synchro Opisto: ${error.message}`);
    } finally {
      setOpistoSyncing(false);
    }
  };

  const handleOpistoBatchSync = async (resume = false) => {
    opistoBatchStopRequested.current = false;
    setOpistoBatchSyncing(true);
    setSuccessMessage('');
    setErrorMessage('');

    const initialState = resume
      ? createBatchState({
          ...opistoBatchProgress,
          status: 'running',
          lastError: null,
          startedAt: opistoBatchProgress.startedAt || new Date().toISOString(),
          lastUpdatedAt: new Date().toISOString()
        })
      : createBatchState({
          status: 'running',
          startedAt: new Date().toISOString(),
          lastUpdatedAt: new Date().toISOString()
        });

    setOpistoBatchProgress(initialState);

    try {
      let offset = resume ? (initialState.nextOffset || 0) : 0;
      let total = resume ? (initialState.total || 0) : 0;
      let processed = resume ? (initialState.processed || 0) : 0;
      let synced = resume ? (initialState.synced || 0) : 0;
      let notFound = resume ? (initialState.notFound || 0) : 0;
      let errorCount = resume ? (initialState.errorCount || 0) : 0;

      while (true) {
        if (opistoBatchStopRequested.current) {
          setOpistoBatchProgress(createBatchState({
            ...initialState,
            processed,
            total,
            nextOffset: offset,
            synced,
            notFound,
            errorCount,
            status: 'paused',
            currentOem: null,
            lastUpdatedAt: new Date().toISOString()
          }));
          setSuccessMessage(`Synchro Opisto mise en pause a ${processed} / ${total || '?'}.`);
          return;
        }

        const result = await fetchJson(`${API_BASE_URL}/import/opisto/algolia-sync-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ offset, limit: 1 })
        });

        total = result.total || total;
        processed = result.nextOffset || processed;
        synced += result.synced || 0;
        notFound += result.notFound || 0;
        errorCount += Array.isArray(result.errors) ? result.errors.length : 0;

        const processedOem = Array.isArray(result.processedOems) ? result.processedOems[0] || null : null;
        setOpistoBatchProgress(createBatchState({
          ...initialState,
          processed,
          total,
          nextOffset: result.nextOffset || processed,
          currentOem: processedOem,
          lastProcessedOem: processedOem,
          synced,
          notFound,
          errorCount,
          lastError: Array.isArray(result.errors) && result.errors.length > 0 ? result.errors[result.errors.length - 1] : null,
          status: result.completed ? 'completed' : 'running',
          lastUpdatedAt: new Date().toISOString()
        }));

        if (result.completed) {
          break;
        }

        offset = result.nextOffset || (offset + 1);
        await new Promise((resolve) => setTimeout(resolve, 75));
      }

      setOpistoBatchProgress((previousState) => createBatchState({
        ...previousState,
        status: 'completed',
        currentOem: null,
        nextOffset: processed,
        lastUpdatedAt: new Date().toISOString()
      }));
      setSuccessMessage(`Synchro Opisto batch terminée : ${processed} / ${total} refOEM traitées.`);
    } catch (error) {
      console.error('Erreur synchro Opisto batch:', error);
      setOpistoBatchProgress((previousState) => createBatchState({
        ...previousState,
        status: 'error',
        currentOem: null,
        lastError: error.message,
        lastUpdatedAt: new Date().toISOString()
      }));
      setErrorMessage(`Erreur lors de la synchro Opisto batch: ${error.message}`);
    } finally {
      setOpistoBatchSyncing(false);
    }
  };

  const handlePauseOpistoBatchSync = () => {
    opistoBatchStopRequested.current = true;
  };

  const handleResetOpistoBatchSync = () => {
    opistoBatchStopRequested.current = true;
    setOpistoBatchSyncing(false);
    setOpistoBatchProgress(createBatchState());
    setSuccessMessage('');
    setErrorMessage('');
    localStorage.removeItem(OPISTO_BATCH_STORAGE_KEY);
  };

  const formatBatchTimestamp = (value) => {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleString('fr-FR');
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', p: 2 }}>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="📊 Veille Marché"
          subheader="Suivi des tendances marché et détection des opportunités"
        />
      </Card>

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/* Résumé Executive */}
          {data.summary && (
            <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
              <Card>
                <CardContent>
                  <div style={{ color: '#666', fontSize: '0.9em' }}>Total Opportunités</div>
                  <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#2196f3' }}>
                    {data.summary.opportunities?.total || 0}
                  </div>
                  <div style={{ fontSize: '0.85em', color: '#999', marginTop: '0.5em' }}>
                    Score moyen: {((data.summary.opportunities?.stats?.avg_score || 0).toFixed(2))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <div style={{ color: '#666', fontSize: '0.9em' }}>OEM Émergentes (30j)</div>
                  <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#4caf50' }}>
                    {data.summary.emerging_oems?.total || 0}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <div style={{ color: '#666', fontSize: '0.9em' }}>Vendeurs Actifs</div>
                  <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#ff9800' }}>
                    {data.sellers.length}
                  </div>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Filtres */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Select
              value={daysFilter}
              onChange={(e) => setDaysFilter(e.target.value)}
              size="small"
            >
              <MenuItem value={7}>7 jours</MenuItem>
              <MenuItem value={30}>30 jours</MenuItem>
              <MenuItem value={90}>90 jours</MenuItem>
              <MenuItem value={180}>180 jours</MenuItem>
            </Select>
            <Button variant="contained" size="small" onClick={loadAllData}>
              Actualiser
            </Button>
          </Box>

          {/* Tabs */}
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Synchronisation" />
                <Tab label="Opportunités OEM" />
                <Tab label="Nouvelles OEM" />
                <Tab label="Métriques sync" />
              </Tabs>
            </Box>

            {/* Tab 0: Synchronisation */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ p: 3 }}>
                <Card sx={{ mb: 3, border: '1px solid #dbe7e5', boxShadow: 'none' }}>
                  <CardHeader
                    title="Synchro Opisto"
                    subheader="Synchronisez `opisto_stock` depuis Opisto.fr pour une refOEM précise."
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      <TextField
                        label="refOEM ciblée"
                        placeholder="Ex: 00007840AS"
                        value={opistoOem}
                        onChange={(event) => setOpistoOem(event.target.value)}
                        size="small"
                        sx={{ minWidth: 260, flex: '1 1 260px' }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleOpistoSync}
                        disabled={opistoSyncing || opistoBatchSyncing || opistoOem.trim() === ''}
                        sx={{ backgroundColor: '#0f766e' }}
                      >
                        {opistoSyncing ? 'Synchro Opisto...' : 'Synchro refOEM'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleOpistoBatchSync(false)}
                        disabled={opistoSyncing || opistoBatchSyncing}
                        sx={{ borderColor: '#0f766e', color: '#0f766e' }}
                      >
                        {opistoBatchSyncing ? 'Synchro stock en cours...' : 'Synchro tout le stock'}
                      </Button>
                    </Box>
                    <Box sx={{ mt: 1, color: '#5f6b6d', fontSize: '0.9rem' }}>
                      La synchro globale passe en batch séquentiel sur la table `stocks`, une demande courte à la fois.
                    </Box>
                    {opistoBatchProgress && (
                      <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                        <Alert severity={
                          opistoBatchProgress.status === 'error'
                            ? 'error'
                            : opistoBatchProgress.status === 'completed'
                              ? 'success'
                              : opistoBatchProgress.status === 'paused'
                                ? 'warning'
                                : 'info'
                        }>
                          Progression: {opistoBatchProgress.processed} / {opistoBatchProgress.total || '?'} refOEM
                          {opistoBatchProgress.currentOem ? ` - en cours: ${opistoBatchProgress.currentOem}` : ''}
                          {opistoBatchProgress.status === 'paused' ? ' - synchro en pause' : ''}
                          {opistoBatchProgress.status === 'error' ? ' - synchro interrompue' : ''}
                        </Alert>
                        <Box sx={{ color: '#334155', fontSize: '0.95rem' }}>
                          Synchros OK: {opistoBatchProgress.synced} | Sans resultat: {opistoBatchProgress.notFound} | Erreurs: {opistoBatchProgress.errorCount}
                        </Box>
                        {opistoBatchProgress.lastProcessedOem && (
                          <Box sx={{ color: '#334155', fontSize: '0.95rem' }}>
                            Dernière refOEM traitée : {opistoBatchProgress.lastProcessedOem}
                          </Box>
                        )}
                        {formatBatchTimestamp(opistoBatchProgress.startedAt) && (
                          <Box sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                            Début : {formatBatchTimestamp(opistoBatchProgress.startedAt)}
                            {formatBatchTimestamp(opistoBatchProgress.lastUpdatedAt) ? ` | Dernière mise à jour : ${formatBatchTimestamp(opistoBatchProgress.lastUpdatedAt)}` : ''}
                          </Box>
                        )}
                        {opistoBatchProgress.lastError && (
                          <Box sx={{ color: '#b91c1c', fontSize: '0.9rem' }}>
                            Dernière erreur : {opistoBatchProgress.lastError}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            onClick={handlePauseOpistoBatchSync}
                            disabled={!opistoBatchSyncing}
                            sx={{ borderColor: '#f59e0b', color: '#b45309' }}
                          >
                            Pause
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => handleOpistoBatchSync(true)}
                            disabled={opistoBatchSyncing || !['paused', 'error'].includes(opistoBatchProgress.status)}
                            sx={{ borderColor: '#0f766e', color: '#0f766e' }}
                          >
                            Reprendre
                          </Button>
                          <Button
                            variant="text"
                            onClick={handleResetOpistoBatchSync}
                            disabled={opistoBatchSyncing}
                            color="inherit"
                          >
                            Reset suivi
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                <EbaySearchCombined />
              </Box>
            </TabPanel>

            {/* Tab 1: Opportunités */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                <strong>Opportunités OEM</strong> — Références absentes de votre stock avec plus de 20 ventes cumulées tous vendeurs confondus.
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>Référence OEM</strong></TableCell>
                      <TableCell align="right"><strong>Ventes totales</strong></TableCell>
                      <TableCell align="right"><strong>Vendeurs</strong></TableCell>
                      <TableCell align="right"><strong>Prix moyen</strong></TableCell>
                      <TableCell align="right"><strong>Prix min / max</strong></TableCell>
                      <TableCell><strong>Vendeurs actifs</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.opportunities.slice(0, 50).map((opp, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                        <TableCell><strong>{opp.oem}</strong></TableCell>
                        <TableCell align="right">
                          <Chip
                            label={opp.total_sales}
                            color={opp.total_sales > 100 ? 'error' : opp.total_sales > 50 ? 'warning' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{opp.vendor_count}</TableCell>
                        <TableCell align="right">€{parseFloat(opp.avg_price || 0).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8em', color: '#666' }}>
                          €{parseFloat(opp.min_price || 0).toFixed(2)} / €{parseFloat(opp.max_price || 0).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8em', color: '#555', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {opp.vendors || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {data.opportunities.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
                  Aucune opportunité détectée (aucune OEM absente du stock avec &gt; 20 ventes)
                </Box>
              )}
              {data.opportunities.length > 50 && (
                <Box sx={{ p: 1, textAlign: 'center', color: '#666', fontSize: '0.85em' }}>
                  Affichage limité aux 50 premières sur {data.opportunities.length} opportunités détectées.
                </Box>
              )}
            </TabPanel>

            {/* Tab 2: Nouvelles OEM */}
            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>OEM</strong></TableCell>
                      <TableCell align="right"><strong>Premier Vendeur</strong></TableCell>
                      <TableCell><strong>Première Apparition</strong></TableCell>
                      <TableCell align="right"><strong>Vendeurs</strong></TableCell>
                      <TableCell align="right"><strong>Prix Médian</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.emergingOems.slice(0, 50).map((oem, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: '#fffacd',
                          '&:hover': { backgroundColor: '#fff8dc' }
                        }}
                      >
                        <TableCell><strong>{oem.oem}</strong></TableCell>
                        <TableCell>{oem.seller_name || '-'}</TableCell>
                        <TableCell>{oem.first_seen || '-'}</TableCell>
                        <TableCell align="right">{oem.seller_count}</TableCell>
                        <TableCell align="right">€{parseFloat(oem.price_median).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {data.emergingOems.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
                  Aucune nouvelle OEM détectée
                </Box>
              )}
            </TabPanel>

            {/* Tab 3: Métriques */}
            <TabPanel value={tabValue} index={3}>
              {data.metrics && (
                <>
                  <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                    <Card>
                      <CardContent>
                        <div style={{ color: '#666', fontSize: '0.9em' }}>Jours de Sync</div>
                        <div style={{ fontSize: '2em', fontWeight: 'bold' }}>
                          {data.metrics.stats?.sync_days || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent>
                        <div style={{ color: '#666', fontSize: '0.9em' }}>Total Articles</div>
                        <div style={{ fontSize: '2em', fontWeight: 'bold' }}>
                          {data.metrics.stats?.total_items || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent>
                        <div style={{ color: '#666', fontSize: '0.9em' }}>Total Appels API</div>
                        <div style={{ fontSize: '2em', fontWeight: 'bold' }}>
                          {data.metrics.stats?.total_api_calls || 0}
                        </div>
                      </CardContent>
                    </Card>
                  </Box>

                  <Card>
                    <CardHeader title="Détail des Synchronisations" />
                    <Divider />
                    <CardContent>
                      <TableContainer>
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell><strong>Date</strong></TableCell>
                              <TableCell><strong>Vendeur</strong></TableCell>
                              <TableCell align="right"><strong>Articles</strong></TableCell>
                              <TableCell align="right"><strong>OEM</strong></TableCell>
                              <TableCell align="right"><strong>Temps (s)</strong></TableCell>
                              <TableCell align="right"><strong>Appels API</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.metrics.metrics?.slice(0, 50).map((metric, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{metric.sync_date}</TableCell>
                                <TableCell>{metric.seller_name}</TableCell>
                                <TableCell align="right">{metric.items_collected}</TableCell>
                                <TableCell align="right">{metric.oem_collected}</TableCell>
                                <TableCell align="right">{metric.execution_time_seconds}</TableCell>
                                <TableCell align="right">{metric.api_calls}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabPanel>
          </Card>
        </>
      )}
    </Box>
  );
}
